import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import PropTypes from 'prop-types';
import { Route, Redirect } from 'react-router-dom';
import queryString from 'query-string';

import ViewportLayout from 'src/component/Layout/ViewportLayout';
import ViewportLayoutPane from 'src/component/Layout/ViewportLayoutPane';
import AppBar from 'src/component/UI/AppBar';
import BottomNavBar from 'src/component/UI/NavBar/BottomNavBar';
import NavBar from 'src/component/UI/NavBar/NavBar';
import NotFoundScreen from 'src/screen/Error/NotFoundScreen';
import ServiceAgreementScreen from 'src/screen/Protected/ServiceAgreementScreen/ServiceAgreementScreen';
import ProfileScreen from 'src/screen/Protected/ProfileScreen/ProfileScreen';
import OfflineBanner from 'src/component/UI/OfflineBanner';
import { prefetchAll } from 'src/module/prefetch/action';
import { getAbilities, getIsAuthenticated, getAcceptedTerms, getUserMustResetPassword, getFeatures } from 'src/module/authentication/selector';
import { getIsPrefetching, getHasInitialized } from 'src/module/prefetch/selector';
import GlobalProgressIndicator from './UI/GlobalProgressIndicator';
import AdminAppBar from './UI/AdminAppBar';
import { onPremEnabled } from 'src/utils/utils';
import { shouldUseChemelexUi } from 'src/module/authentication/chemelexUi.selector';
import { DS_DARK_CLASS, useDsTheme } from 'src/component/ds/DsTheme';
import { cn } from 'src/component/ds/utils';
import DsRoot from 'src/component/ds/DsRoot';
import ChemelexUiBanner from 'src/component/ds/ChemelexUiBanner';
import { DsPanelProvider, DsPanelHost } from 'src/component/ds/panel/DsPanel';


export default function ProtectedRoute (props) {
  const dispatch = useDispatch();

  const {
    adminRoute,
    location,
    component: V1Component,
    v2Component: V2Component,
    v2RequiredFeatures,
    can,
    requiredFeatures,
    requiredClientPreferences,
    cloudOnly,
    ...rest
  } = props;

  const abilities = useSelector(getAbilities);
  const features: any = useSelector(getFeatures);
  const isAuthenticated = useSelector(getIsAuthenticated);
  const isPrefetching = useSelector(getIsPrefetching);
  const hasInitialized = useSelector(getHasInitialized);
  const acceptedTerms = useSelector(getAcceptedTerms);
  const mustResetPassword = useSelector(getUserMustResetPassword);

  const [isOnPremDeployment, setIsOnPremDeployment] = useState<boolean | null>(null);
  const [deploymentTypeLoading, setDeploymentTypeLoading] = useState(true);

  const useChemelexUi = useSelector(shouldUseChemelexUi);

  const isDsDark = useDsTheme() === 'dark';
  const dsPaneClassName = cn('viewport-layout-pane-ds-background', isDsDark && DS_DARK_CLASS);

  useEffect(() => {
    if (isAuthenticated && !isPrefetching && !hasInitialized) {
      dispatch(prefetchAll());
    }
  }, [dispatch, isAuthenticated, hasInitialized, isPrefetching]);

  useEffect(() => {
    async function loadOnPrem () {
      const onPremResult = await onPremEnabled();
      setIsOnPremDeployment(onPremResult);
      setDeploymentTypeLoading(false);
    }
    loadOnPrem();
  }, []);


  const hasPermissions = () => {
    if (!abilities || !features) {
      return false;
    }

    if (!can) {
      return true;
    }
    if (!requiredClientPreferences) {
      return false;
    }

    const hasAllFeatures = requiredFeatures ? requiredFeatures.every(feature => {
      return features.includes(feature);
    }) : true;
    const hasAllAbilities = can.every(ability => {
      return abilities.includes(ability);
    });

    return hasAllAbilities && hasAllFeatures;
  };

  /* The screen shown instead of the route's own, if any. These are all v1
   * screens, so they must not get the v2 chrome either. */
  const getGateScreen = () => {
    if (!hasPermissions()) {
      return (<NotFoundScreen />);
    }
    else if (cloudOnly && (deploymentTypeLoading || isOnPremDeployment)) {
      return (deploymentTypeLoading ? <GlobalProgressIndicator /> : <NotFoundScreen />);
    }
    else if (!acceptedTerms) {
      return (<ServiceAgreementScreen />);
    }
    else if (mustResetPassword) {
      return (<ProfileScreen />);
    }
    return null;
  };

  const gateScreen = getGateScreen();
  const hasV2Version = Boolean(V2Component) && (v2RequiredFeatures ?? []).every(feature => features?.includes(feature));
  const renderV2 = hasV2Version && useChemelexUi && !gateScreen;
  const InnerComponent = renderV2 ? V2Component : V1Component;

  const getProtectedContent = (props) => {
    const content = gateScreen ?? <InnerComponent {...props} />;
    return renderV2 ? <DsRoot className='ds-h-full'>{content}</DsRoot> : content;
  };

  const getMiddlePane = (props) => (
    <ViewportLayoutPane position='middle' className={renderV2 ? dsPaneClassName : 'viewport-layout-pane-standard-background'}>
      {getProtectedContent(props)}
      <ViewportLayoutPane><BottomNavBar adminBar={ adminRoute ? true : false} /></ViewportLayoutPane>
    </ViewportLayoutPane>
  );

  const { jwt, invite } = queryString.parse((location.search || ''));
  const redirectPath = ((invite && jwt) ? `/accept` : '/login');
  return (

    <Route {...rest}
      render={
        (props) => {
          if (!isAuthenticated) {
            return (<Redirect to={{ pathname: redirectPath, search: location.search || '', state: { from: location } }} />);
          } else {
            return (<ViewportLayout>
              <ViewportLayoutPane position='left' className='viewport-layout-main-pane-left'><NavBar adminBar={hasPermissions() && adminRoute} /></ViewportLayoutPane>
              <ViewportLayoutPane position='right' className='viewport-layout-row'>
                <ViewportLayoutPane position='top'>
                  <OfflineBanner />
                  {hasV2Version && !gateScreen ? <ChemelexUiBanner isV2={renderV2} /> : null}
                  {/* v2 screens do not have a header */}
                  {renderV2 ? null : (!adminRoute || !hasPermissions() ? <AppBar /> : <AdminAppBar />)}
                  <GlobalProgressIndicator />
                </ViewportLayoutPane>
                {renderV2 ? (
                  <DsPanelProvider>
                    <div className='ds-flex ds-min-h-0 ds-w-full ds-flex-1'>
                      {getMiddlePane(props)}
                      <DsPanelHost />
                    </div>
                  </DsPanelProvider>
                ) : getMiddlePane(props)}
              </ViewportLayoutPane>
            </ViewportLayout>);
          }
        }
      }
    />

  );
}

ProtectedRoute.propTypes = {
  can: PropTypes.array,
  requiredFeatures: PropTypes.array,
  component: PropTypes.func.isRequired,
  /** Redesigned version of `component`, shown when the user is on the new UI. */
  v2Component: PropTypes.func,
  /** Features needed for `v2Component`, on top of chemelex-ui feature which is in both alpha and beta. */
  v2RequiredFeatures: PropTypes.array,
  path: PropTypes.string,
  location: PropTypes.object,
  requiredClientPreferences: PropTypes.bool,
  adminRoute: PropTypes.bool,
  cloudOnly: PropTypes.bool
};

ProtectedRoute.defaultProps = {
  exact: false,
  requiredClientPreferences: true,
  adminRoute: false,
  cloudOnly: false
};
